import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  IconButton,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Tooltip
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

/**
 * Componente para gestionar campos dinámicos de un estado con drag & drop
 *
 * Props:
 * - camposAsignados: Array de campos ya asignados al estado
 * - catalogoCampos: Array de todos los campos disponibles del catálogo
 * - onCamposChange: Callback cuando cambian los campos
 * - readOnly: Si es true, solo lectura
 */
const SelectorCampos = ({
  camposAsignados = [],
  catalogoCampos = [],
  onCamposChange,
  readOnly = false
}) => {
  const [campos, setCampos] = useState(camposAsignados);
  const [dialogAgregarOpen, setDialogAgregarOpen] = useState(false);
  const [camposDisponibles, setCamposDisponibles] = useState([]);

  useEffect(() => {
    setCampos(camposAsignados);
  }, [camposAsignados]);

  useEffect(() => {
    // Filtrar campos que no están asignados
    const idsAsignados = new Set(campos.map(c => c.id_campo));
    const disponibles = catalogoCampos.filter(c => !idsAsignados.has(c.id_campo));
    setCamposDisponibles(disponibles);
  }, [campos, catalogoCampos]);

  // Manejar reordenamiento con drag & drop
  const handleDragEnd = (result) => {
    if (!result.destination || readOnly) return;

    const items = Array.from(campos);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Actualizar orden_visualizacion
    const camposActualizados = items.map((campo, index) => ({
      ...campo,
      orden_visualizacion: index + 1
    }));

    setCampos(camposActualizados);
    if (onCamposChange) {
      onCamposChange(camposActualizados);
    }
  };

  // Toggle obligatorio
  const handleToggleObligatorio = (id_campo) => {
    if (readOnly) return;

    const camposActualizados = campos.map(campo =>
      campo.id_campo === id_campo
        ? { ...campo, es_obligatorio: !campo.es_obligatorio }
        : campo
    );

    setCampos(camposActualizados);
    if (onCamposChange) {
      onCamposChange(camposActualizados);
    }
  };

  // Eliminar campo
  const handleEliminarCampo = (id_campo) => {
    if (readOnly) return;

    const camposActualizados = campos
      .filter(c => c.id_campo !== id_campo)
      .map((campo, index) => ({
        ...campo,
        orden_visualizacion: index + 1
      }));

    setCampos(camposActualizados);
    if (onCamposChange) {
      onCamposChange(camposActualizados);
    }
  };

  // Agregar campo del catálogo
  const handleAgregarCampo = (campo) => {
    if (readOnly) return;

    const nuevoCampo = {
      id_campo: campo.id_campo,
      nombre_campo: campo.nombre_campo,
      etiqueta_display: campo.etiqueta_display,
      tipo_campo: campo.tipo_campo,
      es_obligatorio: false,
      orden_visualizacion: campos.length + 1,
      configuracion: campo.configuracion_campo
    };

    const camposActualizados = [...campos, nuevoCampo];
    setCampos(camposActualizados);
    if (onCamposChange) {
      onCamposChange(camposActualizados);
    }

    setDialogAgregarOpen(false);
  };

  const getColorTipoCampo = (tipo) => {
    const colores = {
      texto: 'default',
      texto_largo: 'default',
      numero: 'primary',
      decimal: 'primary',
      fecha: 'secondary',
      fecha_hora: 'secondary',
      booleano: 'success',
      archivo: 'warning',
      imagen: 'warning',
      seleccion: 'info',
      seleccion_multiple: 'info',
      ubicacion: 'error'
    };
    return colores[tipo] || 'default';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Campos del Estado ({campos.length})
        </Typography>
        {!readOnly && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogAgregarOpen(true)}
            disabled={camposDisponibles.length === 0}
          >
            Agregar Campo
          </Button>
        )}
      </Box>

      {/* Info */}
      {!readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Arrastra los campos para reordenarlos. Usa el switch para marcar campos como obligatorios.
        </Alert>
      )}

      {/* Lista de campos con drag & drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="campos-list">
          {(provided, snapshot) => (
            <List
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{
                bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'background.paper',
                borderRadius: 1,
                p: 1,
                minHeight: campos.length === 0 ? 100 : 'auto'
              }}
            >
              {campos.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No hay campos asignados a este estado.
                      {!readOnly && ' Haz clic en "Agregar Campo" para comenzar.'}
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                campos.map((campo, index) => (
                  <Draggable
                    key={campo.id_campo}
                    draggableId={String(campo.id_campo)}
                    index={index}
                    isDragDisabled={readOnly}
                  >
                    {(provided, snapshot) => (
                      <Card
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          mb: 1,
                          bgcolor: snapshot.isDragging ? 'action.selected' : 'background.paper',
                          boxShadow: snapshot.isDragging ? 4 : 1,
                          transition: 'all 0.2s'
                        }}
                      >
                        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                          <Grid container alignItems="center" spacing={2}>
                            {/* Drag Handle */}
                            <Grid item>
                              <div {...provided.dragHandleProps}>
                                <DragIcon
                                  sx={{
                                    color: readOnly ? 'action.disabled' : 'action.active',
                                    cursor: readOnly ? 'default' : 'grab'
                                  }}
                                />
                              </div>
                            </Grid>

                            {/* Orden */}
                            <Grid item>
                              <Chip
                                label={index + 1}
                                size="small"
                                sx={{ width: 32 }}
                              />
                            </Grid>

                            {/* Información del campo */}
                            <Grid item xs>
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {campo.etiqueta_display || campo.nombre_campo}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                  <Chip
                                    label={campo.tipo_campo}
                                    size="small"
                                    color={getColorTipoCampo(campo.tipo_campo)}
                                    variant="outlined"
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {campo.nombre_campo}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>

                            {/* Switch obligatorio */}
                            <Grid item>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Obligatorio
                                </Typography>
                                <Switch
                                  checked={campo.es_obligatorio}
                                  onChange={() => handleToggleObligatorio(campo.id_campo)}
                                  disabled={readOnly}
                                  color="primary"
                                />
                              </Box>
                            </Grid>

                            {/* Botón eliminar */}
                            {!readOnly && (
                              <Grid item>
                                <Tooltip title="Eliminar campo">
                                  <IconButton
                                    onClick={() => handleEliminarCampo(campo.id_campo)}
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Card>
                    )}
                  </Draggable>
                ))
              )}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>

      {/* Dialog para agregar campos */}
      <Dialog
        open={dialogAgregarOpen}
        onClose={() => setDialogAgregarOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agregar Campo del Catálogo</DialogTitle>
        <DialogContent>
          {camposDisponibles.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No hay más campos disponibles para agregar.
            </Typography>
          ) : (
            <List>
              {camposDisponibles.map((campo) => (
                <ListItem
                  key={campo.id_campo}
                  button
                  onClick={() => handleAgregarCampo(campo)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <ListItemText
                    primary={campo.etiqueta_display || campo.nombre_campo}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={campo.tipo_campo}
                          size="small"
                          color={getColorTipoCampo(campo.tipo_campo)}
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {campo.nombre_campo}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={campo.descripcion || 'Sin descripción'}>
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAgregarOpen(false)}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SelectorCampos;
